import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { LogArgument } from 'rollbar';

import { env } from '@/env';
import { AuthAdapter } from '@/features/auth/lib/auth-adapter';
import { AppError } from '@/lib/error';
import { rollbar } from '@/lib/rollbar';

let authAdapter: AuthAdapter | null = null;

export function setApiAuthAdapter(adapter: AuthAdapter): void {
  authAdapter = adapter;
}

export const apiClient = axios.create({ baseURL: env.NEXT_PUBLIC_BACKEND_BASE_URL });

apiClient.interceptors.request.use(async (config) => {
  if (authAdapter) {
    const token = await authAdapter.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    // Retry once on 401 with a fresh token (Firebase/OIDC getAccessToken auto-refreshes expired tokens)
    if (status === 401 && !originalRequest._retry && authAdapter) {
      originalRequest._retry = true;
      const freshToken = await authAdapter.getAccessToken();
      if (freshToken) {
        originalRequest.headers.Authorization = `Bearer ${freshToken}`;
        return apiClient(originalRequest);
      }
    }

    const appError = toAppError(error);

    if (appError.shouldLog()) {
      rollbar.error(appError.message, error, appError.rollbarContext as LogArgument);
    }

    throw appError;
  },
);

function toAppError(error: AxiosError): AppError {
  if (!error.response) {
    return new AppError('internalError', 'Network error', { cause: error });
  }

  const data = error.response.data as Record<string, unknown> | null;

  // Server always returns structured AppError JSON — reconstruct it to preserve validation details
  if (data && typeof data.error === 'string') {
    return AppError.fromJSON(data, 'Request failed');
  }

  // Fallback: map HTTP status code to AppError type
  const status = error.response.status;
  if (status === 400) return new AppError('badRequest', 'Bad request', { cause: error });
  if (status === 401) return new AppError('unauthorized', 'Unauthorized', { cause: error });
  if (status === 403) return new AppError('forbidden', 'Forbidden', { cause: error });
  if (status === 404) return new AppError('notFound', 'Not found', { cause: error });
  if (status === 409) return new AppError('conflict', 'Conflict', { cause: error });
  if (status === 422) return new AppError('validationError', 'Validation error', { cause: error });
  if (status === 502) return new AppError('badGateway', 'Bad gateway', { cause: error });

  return new AppError('internalError', `Request failed with status ${status}`, { cause: error });
}
